import React, { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button';
import { CategoryService } from '../../handling_data/services/CategoryService';
import { CategoryProxy } from '../../handling_data/proxies/CategoryProxy';
import { CategoryModal } from './CategoryModal';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { AlertModal } from '../../components/ui/AlertModal';

export const CategoriesList: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [categories, setCategories] = useState<any[]>([]);
    const [rawCategories, setRawCategories] = useState<any[]>([]); // To store original objects for editing
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<any>(null);

    // Confirm & Alert Modal States
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: 0 });
    const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', type: 'info' as any });
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, [i18n.language]);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await CategoryService.getCategories();
            setRawCategories(response.data);
            const formatted = CategoryProxy.formatListForUI(response.data, i18n.language);
            setCategories(formatted);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        setConfirmModal({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        const id = confirmModal.id;
        try {
            setIsDeleting(true);
            await CategoryService.deleteCategory(id);
            setConfirmModal({ isOpen: false, id: 0 });
            fetchCategories(); // Refresh list
        } catch (error: any) {
            setConfirmModal({ isOpen: false, id: 0 });
            if (error.response?.status === 422) {
                const serverMsg = error.response.data.message || '';
                const match = serverMsg.match(/\d+/);
                const count = match ? match[0] : '?';
                setAlertModal({
                    isOpen: true,
                    title: t('common.error') || 'خطأ',
                    message: t('categories_page.table.delete_error_linked', { count }),
                    type: 'error'
                });
            } else {
                console.error('Failed to delete category:', error);
                setAlertModal({
                    isOpen: true,
                    title: t('common.error') || 'خطأ',
                    message: t('common.error_occurred') || 'حدث خطأ ما',
                    type: 'error'
                });
            }
        } finally {
            setIsDeleting(false);
        }
    };

    const handleEdit = (id: number) => {
        const category = rawCategories.find(c => c.id === id);
        setSelectedCategory(category);
        setIsModalOpen(true);
    };

    const handleAdd = () => {
        setSelectedCategory(null);
        setIsModalOpen(true);
    };

    const filteredCategories = categories.filter(cat =>
        cat.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-primary">{t('categories_page.title')}</h2>
                    <p className="text-v3-text-secondary text-sm">{t('categories_page.subtitle')}</p>
                </div>
                <Button
                    onClick={handleAdd}
                    className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                >
                    <span className="w-5 h-5">➕</span>
                    <span>{t('categories_page.add_new')}</span>
                </Button>
            </div>

            {/* Search */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-v3-border">
                <div className="relative">
                    <span className={`absolute ${i18n.dir() === 'rtl' ? 'right-3' : 'left-3'} top-3 w-5 h-5 text-gray-400`}>🔍</span>
                    <input
                        type="text"
                        placeholder={t('categories_page.search_placeholder')}
                        className={`w-full ${i18n.dir() === 'rtl' ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Categories Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-v3-border overflow-hidden min-h-[300px] flex flex-col">
                {loading ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-3">
                        <span className="w-8 h-8 animate-spin text-primary">⌛</span>
                        <span>{t('common.loading')}...</span>
                    </div>
                ) : filteredCategories.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-right">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    <th className={`py-4 px-6 font-semibold text-gray-600 text-sm ${i18n.dir() === 'rtl' ? 'text-right' : 'text-left'}`}>الاسم (AR)</th>
                                    <th className={`py-4 px-6 font-semibold text-gray-600 text-sm ${i18n.dir() === 'rtl' ? 'text-right' : 'text-left'}`}>Name (EN)</th>
                                    <th className={`py-4 px-6 font-semibold text-gray-600 text-sm ${i18n.dir() === 'rtl' ? 'text-right' : 'text-left'}`}>İsim (TR)</th>
                                    <th className="py-4 px-6 font-semibold text-gray-600 text-sm text-center">{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredCategories.map((category) => (
                                    <tr key={category.id} className="hover:bg-gray-50/50 transition-colors group">
                                        <td className="py-4 px-6 font-bold text-gray-800">
                                            {category.name_ar}
                                        </td>
                                        <td className="py-4 px-6 text-gray-600 font-medium">
                                            {category.name_en}
                                        </td>
                                        <td className="py-4 px-6 text-gray-600 font-medium">
                                            {category.name_tr}
                                        </td>
                                        <td className="py-4 px-6">
                                            <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => handleEdit(category.id)}
                                                    className="w-8 h-8 flex items-center justify-center hover:bg-blue-50 text-blue-600 rounded-lg transition-colors border border-transparent hover:border-blue-100"
                                                    title={t('common.edit')}
                                                >
                                                    <span className="w-4 h-4">📝</span>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(category.id)}
                                                    className="w-8 h-8 flex items-center justify-center hover:bg-red-50 text-red-600 rounded-lg transition-colors border border-transparent hover:border-red-100"
                                                    title={t('common.delete')}
                                                >
                                                    <span className="w-4 h-4">🗑️</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-20">
                        <span className="w-12 h-12 mb-4 opacity-20">🔍</span>
                        <p>{t('categories_page.no_data') || 'لا توجد تصنيفات حالياً'}</p>
                    </div>
                )}
            </div>

            {/* Category Modal */}
            <CategoryModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={fetchCategories}
                category={selectedCategory}
            />

            {/* Confirm Delete Modal */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, id: 0 })}
                onConfirm={confirmDelete}
                title={t('common.confirm_delete') || 'تأكيد الحذف'}
                message={t('common.confirm_delete_msg') || 'هل أنت متأكد من رغبتك في حذف هذا العنصر؟ لا يمكن التراجع عن هذا الإجراء.'}
                loading={isDeleting}
            />

            {/* Alert Modal */}
            <AlertModal
                isOpen={alertModal.isOpen}
                onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
                title={alertModal.title}
                message={alertModal.message}
                type={alertModal.type}
            />
        </div>
    );
};
